import React, { useState } from 'react';
import { Plus, Edit, Trash, DollarSign } from 'lucide-react';
import Table from '../components/Table.tsx';
import Modal from '../components/Modal.tsx';
import { toast } from 'react-hot-toast';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import api from '../utils/axios.ts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PermissionPage, PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

const feeTypeOptions = [
  { value: 'application', label: 'Application Fee' },
  { value: 'processing', label: 'Processing Fee' },
  { value: 'insurance', label: 'Insurance Fee' },
  { value: 'appraisal', label: 'Appraisal Fee' },
  { value: 'legal', label: 'Legal Fee' },
  { value: 'administration', label: 'Administration Fee' },
  { value: 'other', label: 'Other Fee' },
];

const calculationTypeOptions = [
  { value: 'fixed', label: 'Fixed Amount (KES)' },
  { value: 'percentage', label: 'Percentage of Loan Amount (%)' },
];

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-KE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

// Custom hooks for API calls
const useLoanFees = () => {
  return useQuery({
    queryKey: ['loan-fees'],
    queryFn: async () => {
      const { data } = await api.get('/loan-fees');
      return data;
    },
  });
};

const useLoanTypes = () => {
  return useQuery({
    queryKey: ['loan-types-simple'],
    queryFn: async () => {
      const { data } = await api.get('/loan-type');
      return data;
    },
  });
};

const useCreateLoanFee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (feeData) => {
      const { data } = await api.post('/loan-fees', feeData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan-fees'] });
      toast.success('Loan fee created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create loan fee');
    },
  });
};

const useUpdateLoanFee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...feeData }) => {
      const { data } = await api.put(`/loan-fees/${id}`, feeData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan-fees'] });
      toast.success('Loan fee updated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update loan fee');
    },
  });
};

const useDeleteLoanFee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await api.delete(`/loan-fees/${id}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loan-fees'] });
      toast.success('Loan fee deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete loan fee');
    },
  });
};

export default function LoanFeesPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const isAdmin = useIsAdmin();
  const { can } = usePermissions();

  const { data: feesData, isLoading } = useLoanFees();
  const { data: loanTypesData } = useLoanTypes();
  const createFee = useCreateLoanFee();
  const updateFee = useUpdateLoanFee();
  const deleteFee = useDeleteLoanFee();

  const columns = [
    {
      label: 'Fee Name',
      field: 'name',
      sortable: true,
      className: 'px-4 py-3 text-sm',
      render: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.name}</div>
          <div className="text-xs text-gray-500">{row.description}</div>
        </div>
      ),
    },
    {
      label: 'Type',
      field: 'fee_type',
      sortable: true,
      className: 'px-4 py-3 text-sm',
      render: (row) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
          {row.fee_type}
        </span>
      ),
    },
    {
      label: 'Amount',
      field: 'amount',
      sortable: true,
      className: 'px-4 py-3 text-sm text-right',
      render: (row) => (
        <div className="font-semibold text-gray-900">
          {row.calculation_type === 'percentage' 
            ? `${row.amount}%` 
            : `KES ${formatCurrency(row.amount)}`}
        </div>
      ),
    },
    {
      label: 'Calculation',
      field: 'calculation_type',
      sortable: true,
      className: 'px-4 py-3 text-sm',
      render: (row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.calculation_type === 'percentage' 
            ? 'bg-purple-100 text-purple-800' 
            : 'bg-green-100 text-green-800'
        }`}>
          {row.calculation_type === 'percentage' ? 'Percentage' : 'Fixed'}
        </span>
      ),
    },
    {
      label: 'Applied to Loan Types',
      field: 'loan_types',
      className: 'px-4 py-3 text-sm',
      render: (row) => {
        const loanTypeCount = row.loan_types?.length || 0;
        return (
          <div>
            {loanTypeCount > 0 ? (
              <span className="text-gray-700">
                {loanTypeCount} loan type{loanTypeCount > 1 ? 's' : ''}
              </span>
            ) : (
              <span className="text-gray-400 text-xs">Not applied</span>
            )}
          </div>
        );
      },
    },
    {
      label: 'Status',
      field: 'is_active',
      sortable: true,
      className: 'px-4 py-3 text-sm',
      render: (row) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          row.is_active 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {row.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      label: 'Actions',
      field: 'actions',
      className: 'px-4 py-3 text-sm',
      render: (row) => (
        <div className="flex space-x-2">
          {can(PERMISSIONS.MANAGE_LOAN_TYPES) && (
            <>
              <button
                onClick={() => {
                  setSelectedFee(row);
                  setIsModalOpen(true);
                }}
                className="text-blue-600 hover:text-blue-800"
                title="Edit"
              >
                <Edit size={18} />
              </button>
              <button
                onClick={() => handleDeleteFee(row.id)}
                className="text-red-600 hover:text-red-800"
                title="Delete"
              >
                <Trash size={18} />
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const handleCreateOrUpdateFee = (feeData) => {
    if (selectedFee) {
      updateFee.mutate(
        { id: selectedFee.id, ...feeData },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            setSelectedFee(null);
          },
        }
      );
    } else {
      createFee.mutate(feeData, {
        onSuccess: () => {
          setIsModalOpen(false);
        },
      });
    }
  };

  const handleDeleteFee = (id) => {
    if (window.confirm('Are you sure you want to delete this loan fee?')) {
      deleteFee.mutate(id);
    }
  };

  return (
    <PermissionPage permission={PERMISSIONS.MANAGE_LOAN_TYPES}>
      <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div className="flex items-center space-x-3">
            <DollarSign size={28} className="text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold">Loan Processing Fees</h2>
              <p className="text-sm text-gray-600">Manage fees that can be applied to loan types</p>
            </div>
          </div>
          <PermissionGate permission={PERMISSIONS.MANAGE_LOAN_TYPES}>
            <button
              onClick={() => {
                setSelectedFee(null);
                setIsModalOpen(true);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus size={16} className="mr-2" />
              Add Loan Fee
            </button>
          </PermissionGate>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 kpi-grid">
          <div className="bg-white rounded-xl shadow p-6">
            <p className="text-sm text-gray-500 mb-1">Total Fees</p>
            <h3 className="text-2xl font-bold text-blue-600">{feesData?.data?.length || 0}</h3>
          </div>
          <div className="bg-white rounded-xl shadow p-6">
            <p className="text-sm text-gray-500 mb-1">Active Fees</p>
            <h3 className="text-2xl font-bold text-green-600">
              {feesData?.data?.filter(f => f.is_active).length || 0}
            </h3>
          </div>
          <div className="bg-white rounded-xl shadow p-6">
            <p className="text-sm text-gray-500 mb-1">Inactive Fees</p>
            <h3 className="text-2xl font-bold text-gray-600">
              {feesData?.data?.filter(f => !f.is_active).length || 0}
            </h3>
          </div>
        </div>

        {/* Fees Table */}
        <div className="bg-white rounded-xl shadow">
          <Table
            columns={columns}
            data={feesData?.data || []}
            loading={isLoading}
            page={1}
            perPage={50}
            total={feesData?.data?.length || 0}
            onPageChange={() => {}}
          />
        </div>

        {/* Fee Form Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedFee(null);
          }}
          title={selectedFee ? 'Edit Loan Fee' : 'Add Loan Fee'}
        >
          <LoanFeeForm
            initialData={selectedFee}
            loanTypes={loanTypesData?.data || []}
            onSubmit={handleCreateOrUpdateFee}
            onCancel={() => {
              setIsModalOpen(false);
              setSelectedFee(null);
            }}
          />
        </Modal>
      </div>
    </div>
    </PermissionPage>
  );
}

function LoanFeeForm({ initialData, loanTypes, onSubmit, onCancel }) {
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [feeType, setFeeType] = useState(initialData?.fee_type || 'application');
  const [amount, setAmount] = useState(initialData?.amount || '');
  const [calculationType, setCalculationType] = useState(initialData?.calculation_type || 'fixed');
  const [isActive, setIsActive] = useState(initialData?.is_active ?? true);
  const [selectedLoanTypes, setSelectedLoanTypes] = useState(initialData?.loan_type_ids || []);

  const toggleLoanType = (loanTypeId) => {
    setSelectedLoanTypes(prev =>
      prev.includes(loanTypeId)
        ? prev.filter(id => id !== loanTypeId)
        : [...prev, loanTypeId]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (calculationType === 'percentage' && parseFloat(amount) > 100) {
      toast.error('Percentage amount cannot exceed 100%');
      return;
    }

    if (parseFloat(amount) < 0) {
      toast.error('Amount cannot be negative');
      return;
    }

    onSubmit({
      name,
      description,
      fee_type: feeType,
      amount: parseFloat(amount),
      calculation_type: calculationType,
      is_active: isActive,
      loan_type_ids: selectedLoanTypes,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Fee Name *</label>
        <input
          type="text"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g., Standard Application Fee"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="Brief description of this fee"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Fee Type *</label>
        <select
          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
          value={feeType}
          onChange={(e) => setFeeType(e.target.value)}
          required
        >
          {feeTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Calculation Type *</label>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            value={calculationType}
            onChange={(e) => setCalculationType(e.target.value)}
            required
          >
            {calculationTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Amount * {calculationType === 'percentage' ? '(%)' : '(KES)'}
          </label>
          <input
            type="number"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            min={0}
            max={calculationType === 'percentage' ? 100 : undefined}
            step={calculationType === 'percentage' ? 0.01 : 1}
            placeholder={calculationType === 'percentage' ? 'e.g., 2.5' : 'e.g., 500'}
          />
        </div>
      </div>

      {/* Fee Preview */}
      {amount && (
        <div className="bg-blue-50 p-3 rounded-lg text-sm">
          <p className="font-medium text-gray-700 mb-1">Fee Preview:</p>
          <p className="text-gray-600">
            {calculationType === 'percentage'
              ? `${amount}% of the loan amount will be charged`
              : `KES ${formatCurrency(parseFloat(amount))} will be charged`}
          </p>
        </div>
      )}

      {/* Apply to Loan Types */}
      <div className="border-t pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Apply to Loan Types (Optional)
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Select which loan types this fee should be applied to. Leave empty to apply manually later.
        </p>
        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3 space-y-2">
          {loanTypes.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500 mb-2">No loan types available for your group</p>
              <p className="text-xs text-gray-400">Create loan types first before applying fees to them</p>
            </div>
          ) : (
            loanTypes.map((loanType) => (
              <label key={loanType.id} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                <input
                  type="checkbox"
                  checked={selectedLoanTypes.includes(loanType.id)}
                  onChange={() => toggleLoanType(loanType.id)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{loanType.name}</span>
              </label>
            ))
          )}
        </div>
        {selectedLoanTypes.length > 0 && (
          <p className="text-xs text-gray-600 mt-2">
            {selectedLoanTypes.length} loan type{selectedLoanTypes.length > 1 ? 's' : ''} selected
          </p>
        )}
      </div>

      {/* Active Status */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="isActive"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
          Active (fee will be applied to new loans)
        </label>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          {initialData ? 'Update' : 'Create'} Fee
        </button>
      </div>
    </form>
  );
}
